import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { cn } from "@/cn";
import { useProfile } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import { UserAvatar } from "@/components/user-avatar";

/** Compact account chip with dropdown (profile, AI providers, sign out). */
export function UserMenu({ className }: { className?: string }) {
  const { user, signOut } = useAuth();
  const { data: profile } = useProfile();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const displayName =
    profile?.name?.trim() ||
    user?.name?.trim() ||
    user?.email?.trim() ||
    "Account";
  const email = profile?.email?.trim() || user?.email?.trim() || "";

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div className={cn("relative shrink-0", className)} ref={menuRef}>
      <button
        type="button"
        onClick={() => setMenuOpen((o) => !o)}
        className="inline-flex items-center gap-2 rounded-md py-1 pl-1.5 pr-2 transition-colors hover:bg-surface-hover"
        aria-expanded={menuOpen}
        aria-haspopup="menu"
      >
        <UserAvatar
          name={displayName}
          email={email}
          src={profile?.avatarUrl}
          className="size-8 text-[11px]"
        />
        <span className="min-w-0 text-left leading-tight">
          <span className="block max-w-40 truncate text-[13px] font-medium text-foreground">
            {displayName}
          </span>
          {email && (
            <span className="block max-w-40 truncate text-[11px] text-faint">
              {email}
            </span>
          )}
        </span>
        <ChevronDown
          className={cn(
            "size-3.5 text-muted-foreground transition-transform",
            menuOpen && "rotate-180",
          )}
        />
      </button>
      {menuOpen && (
        <div
          role="menu"
          className="absolute right-0 z-40 mt-1.5 w-60 rounded-md border border-border bg-popover py-1.5 shadow-xl ring-1 ring-black/5 dark:ring-white/10"
        >
          <div className="border-b border-border px-3 pb-2 pt-1">
            <p className="truncate text-sm font-medium text-foreground">{displayName}</p>
            {email && (
              <p className="truncate text-xs text-faint">{email}</p>
            )}
          </div>
          <Link
            to="/settings/account"
            role="menuitem"
            className="block px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-surface-hover"
            onClick={() => setMenuOpen(false)}
          >
            Profile
          </Link>
          <Link
            to="/settings/ai"
            role="menuitem"
            className="block px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-surface-hover"
            onClick={() => setMenuOpen(false)}
          >
            AI providers
          </Link>
          <button
            type="button"
            role="menuitem"
            className="block w-full px-3 py-1.5 text-left text-sm text-foreground transition-colors hover:bg-surface-hover"
            onClick={() => {
              setMenuOpen(false);
              void signOut();
            }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
